<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStoryGameEditor } from "@/stores/storyGameEditor";
import { showLoadingToast } from "vant";
import { useNodeTypeText, usePreviewImg } from "@/use/common";
import { useI18n } from "vue-i18n";
import DialogPreviewItem from "@/components/mobile/gal-game/editor/components/DialogPreviewItem.vue";

// Props & Emit
const props = defineProps<{
  storyGameId: number;
  nodeId: number;
  dialogId: number;
}>();
const emit = defineEmits<{
  (e: "update:nodeId", nodeId: number): void;
  (e: "update:dialogId", dialogId: number): void;
  (e: "onDialogClick", dialogId: number, nodeId: number): void;
}>();
// State
const { t } = useI18n();
const storyGameEditorStore = useStoryGameEditor();
const dialogRefs = ref<HTMLDivElement[]>([]);
const node = computed(() => storyGameEditorStore.nodeMap[props.nodeId]);
const nodeList = computed(() =>
  storyGameEditorStore.nodes.map((n) => storyGameEditorStore.nodeMap[n])
);
const dialogList = computed(() =>
  storyGameEditorStore.dialogList.map((d) => storyGameEditorStore.dialogMap[d])
);

// Lifecycle
onMounted(async () => {
  const loading = showLoadingToast("");
  try {
    await storyGameEditorStore.initNode(props.storyGameId);
    await storyGameEditorStore.initDialog(props.nodeId);
    nextTick(scrollToDialog);
  } finally {
    loading.close();
  }
});

watch(
  () => props.nodeId,
  async (nodeId) => {
    const loading = showLoadingToast("");
    try {
      storyGameEditorStore.isInitDialog = false;
      await storyGameEditorStore.initDialog(nodeId);
    } finally {
      loading.close();
    }
  }
);
// Methods
const scrollToDialog = () => {
  const idx = dialogList.value.findIndex((d) => d.id === props.dialogId);
  if (idx !== -1) {
    const dialogRef = dialogRefs.value![idx];
    dialogRef?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};
const onChangeNode = async (e: Event) => {
  const nodeId = Number((e.target as HTMLSelectElement).value);
  emit("update:nodeId", nodeId);
};

const getPreviewUrl = (dialog: StoryGameDialog) => {
  if (dialog.previewMedia) {
    return usePreviewImg(dialog.previewMedia.url);
  }
  if (dialog.sceneMedia) {
    return usePreviewImg(dialog.sceneMedia.url);
  }
  return "";
};

const goToDialog = (nodeId: number) => {
  emit("update:nodeId", nodeId);
  scrollToDialog();
};

export interface DialogPreviewInstance {
  goToDialog: typeof goToDialog;
}

defineExpose<DialogPreviewInstance>({
  goToDialog,
});
</script>
<template>
  <div class="w-full overflow-hidden flex flex-col bg-base-100">
    <div class="flex items-center gap-1 rounded px-2 py-1">
      <select class="select select-sm select-bordered" @change="onChangeNode">
        <option
          v-for="(item, idx) in nodeList"
          :key="item.id"
          :disabled="item.id === props.nodeId"
          :selected="item.id === props.nodeId"
          :value="item.id"
        >
          {{ idx + 1 }}. {{ useNodeTypeText(item.type) }}:
          {{ item.name ? item.name : t("game.unnamedNode") }}
        </option>
      </select>
    </div>
    <div class="w-full overflow-x-scroll">
      <div class="flex gap-1 p-2">
        <div v-for="(item, idx) in dialogList" :key="item.id" class="relative">
          <dialog-preview-item
            :class="{
              'border-primary': item.id === dialogId,
              'border-transparent': item.id !== dialogId,
            }"
            :dialog="item"
            :index="idx"
            @click="emit('onDialogClick', item.id!, item.storyNodeId!)"
          />
        </div>
      </div>
      <div
        v-if="dialogList.length === 0"
        class="w-32 md:w-48 aspect-[16/9] flex items-center justify-center bg-base-100 rounded"
      >
        {{ t("game.noDialog") }}
      </div>
    </div>
  </div>
</template>
